/**
 * useSprintActions Hook
 * 
 * Composes all sprint/task/group modification logic from focused sub-hooks.
 * Keeps the main component focused on rendering.
 * 
 * @param {Object} localSprint - Current sprint state
 * @param {Function} setLocalSprint - State setter for local sprint
 * @param {Array} allSprints - All sprints for nested sprint operations
 * @param {Function} recordAction - Function to record undoable actions
 * @param {Object} options - Additional options
 * @param {Function} options.onUpdateSprint - Callback to update a sprint globally
 */

import { useMemo } from 'react';
import { useTaskActions } from './taskActions';
import { useGroupActions } from './useGroupActions';
import { useTagActions } from './useTagActions';
import { useNestedSprintActions } from './useNestedSprintActions';

export const useSprintActions = (localSprint, setLocalSprint, allSprints = [], recordAction = null, options = {}) => {
  const { onUpdateSprint, onDateValidationError } = options;
  
  // Get nested sprints for current sprint (for name sync when renaming tasks)
  const nestedSprints = useMemo(() => {
    return allSprints.filter(s => s.parentSprintId === localSprint?.id);
  }, [allSprints, localSprint?.id]);
  
  // Compose all action hooks - pass recordAction for undo support
  // Pass nested sprints and onUpdateSprint for name sync when renaming tasks with nested campaigns
  const taskActions = useTaskActions(localSprint, setLocalSprint, recordAction, {
    nestedSprints,
    onUpdateSprint,
    onDateValidationError,
  });
  const groupActions = useGroupActions(localSprint, setLocalSprint);
  const tagActions = useTagActions(localSprint, setLocalSprint, recordAction);
  
  // Get all categories for nested sprint icon color resolution
  const allCategories = tagActions.getAllCategories();
  const nestedSprintActions = useNestedSprintActions(localSprint, setLocalSprint, allSprints, allCategories);

  return {
    // Task actions
    handleTaskStatusChange: taskActions.handleTaskStatusChange,
    handleDependencyChange: taskActions.handleDependencyChange,
    handleConditionsChange: taskActions.handleConditionsChange,
    handleAddTask: taskActions.handleAddTask,
    handleCreateConnectedTask: taskActions.handleCreateConnectedTask,
    handleInsertNodeOnEdge: taskActions.handleInsertNodeOnEdge,
    handleEditTask: taskActions.handleEditTask,
    handleDeleteTask: taskActions.handleDeleteTask,
    handleDeleteTasks: taskActions.handleDeleteTasks,
    handleDuplicateTask: taskActions.handleDuplicateTask,
    handleRenameTask: taskActions.handleRenameTask,
    handleTaskResize: taskActions.handleTaskResize,
    handleTaskResizeEnd: taskActions.handleTaskResizeEnd,
    handleIconChange: taskActions.handleIconChange,
    clearDeletedIconFromAllTasks: taskActions.clearDeletedIconFromAllTasks,
    handleDecisionAnswerChange: taskActions.handleDecisionAnswerChange,
    handleDecisionOptionsChange: taskActions.handleDecisionOptionsChange,
    handleNotesChange: taskActions.handleNotesChange,
    handleInternalNotesChange: taskActions.handleInternalNotesChange,
    handleMissionDocChange: taskActions.handleMissionDocChange,
    handleMissionDocTicksChange: taskActions.handleMissionDocTicksChange,
    handleMissionDocFilesChange: taskActions.handleMissionDocFilesChange,
    handleMissionVariablesChange: taskActions.handleMissionVariablesChange,
    handleAttachedDocsChange: taskActions.handleAttachedDocsChange,
    handleQuickNotesChange: taskActions.handleQuickNotesChange,
    handleMissionDocsMigrated: taskActions.handleMissionDocsMigrated,
    handleClearMissionLegacyNotes: taskActions.handleClearMissionLegacyNotes,
    handleDeliverablesChange: taskActions.handleDeliverablesChange,
    handleInputsChange: taskActions.handleInputsChange,
    handleDueDateChange: taskActions.handleDueDateChange,
    handleEstimatedDurationMinutesChange: taskActions.handleEstimatedDurationMinutesChange,
    handleCreatedAtChange: taskActions.handleCreatedAtChange,
    handleStartedAtChange: taskActions.handleStartedAtChange,
    handleCompletedAtChange: taskActions.handleCompletedAtChange,
    handleUpdatedAtChange: taskActions.handleUpdatedAtChange,
    handlePriorityChange: taskActions.handlePriorityChange,
    handleDifficultyChange: taskActions.handleDifficultyChange,
    handleSetStatusAutoSync: taskActions.handleSetStatusAutoSync,
    handleMilestoneAnchorChange: taskActions.handleMilestoneAnchorChange,
    handleTaskStartDateChange: taskActions.handleTaskStartDateChange,
    handleTaskEndDateChange: taskActions.handleTaskEndDateChange,
    handleTaskDateRangeChange: taskActions.handleTaskDateRangeChange,
    handleRemindersChange: taskActions.handleRemindersChange,
    handleAttachmentCountChange: taskActions.handleAttachmentCountChange,
    handleCustomFieldValueChange: taskActions.handleCustomFieldValueChange,
    
    // Archive actions
    handleArchiveTask: taskActions.handleArchiveTask,
    handleArchiveTasks: taskActions.handleArchiveTasks,
    handleUnarchiveTask: taskActions.handleUnarchiveTask,
    handleAutoArchiveCompletedTasks: taskActions.handleAutoArchiveCompletedTasks,
    
    // Group actions
    handleAddGroup: groupActions.handleAddGroup,
    handleEditGroup: groupActions.handleEditGroup,
    handleChangeGroupIcon: groupActions.handleChangeGroupIcon,
    handleChangeGroupColor: groupActions.handleChangeGroupColor,
    handleDeleteGroup: groupActions.handleDeleteGroup,
    
    // Tag actions (new multi-select system)
    handleAddTag: tagActions.handleAddTag,
    handleRemoveTag: tagActions.handleRemoveTag,
    handleSetTags: tagActions.handleSetTags,
    handleSetPrimaryTag: tagActions.handleSetPrimaryTag,
    handleRenameTag: tagActions.handleRenameTag,
    handleDeleteTag: tagActions.handleDeleteTag,
    getAllTags: tagActions.getAllTags,
    
    // Legacy category actions (backwards compat - map to tag operations)
    handleCategoryChange: tagActions.handleCategoryChange,
    handleRenameCategory: tagActions.handleRenameCategory,
    handleDeleteCategory: tagActions.handleDeleteCategory,
    getAllCategories: tagActions.getAllCategories,
    
    // Nested sprint actions
    handleCreateNestedSprint: nestedSprintActions.handleCreateNestedSprint,
    handleRemoveNestedSprint: nestedSprintActions.handleRemoveNestedSprint,
    canHaveNestedSprints: nestedSprintActions.canHaveNestedSprints,
  };
};

export default useSprintActions;
